// PLAN-cockpit-v2 Phase 0-C: 共通 Markdown (typography 強化、検索語 highlight 対応)
//
// 検索語 highlight は FTS / hybrid モード時のみ機能 (props.highlightTerms)、
// embedding モード時は no-op (cosine は term ベースでないため、PLAN レビュー M3 反映)。

import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'

interface MarkdownProps {
  source: string
  highlightTerms?: string[]
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** マッチした term を <mark> に変換した文字列を返す (Markdown source 段階で適用) */
function markTerms(source: string, terms: string[]): string {
  if (terms.length === 0) return source
  // code block / inline code は除外したいが簡易実装で全文置換 (誤差は許容)
  // 後で plugin 化する余地あり
  let out = source
  for (const t of terms) {
    if (!t || t.length < 2) continue
    const re = new RegExp(`(${escapeRegExp(t)})`, 'gi')
    out = out.replace(re, '==$1==')
  }
  return out
}

export function Markdown({ source, highlightTerms = [] }: MarkdownProps) {
  const text = useMemo(() => markTerms(source, highlightTerms), [source, highlightTerms])

  return (
    <div className="prose prose-sm max-w-none text-text-base
      prose-headings:text-text-base prose-headings:font-medium
      prose-p:text-text-base prose-p:leading-relaxed
      prose-strong:text-text-base
      prose-a:text-accent-primary prose-a:no-underline hover:prose-a:underline
      prose-code:bg-bg-overlay prose-code:px-1 prose-code:py-0.5 prose-code:rounded
      prose-code:text-text-base prose-code:before:content-none prose-code:after:content-none
      prose-pre:bg-bg-overlay prose-pre:text-text-base
      prose-blockquote:text-text-muted prose-blockquote:border-l-border-strong
      prose-li:text-text-base
      prose-hr:border-border-subtle">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          // ==text== を <mark> に
          // remark-gfm の delete (~~text~~) に近い syntax を独自に
          p: ({ children }) => <p>{renderHighlight(children)}</p>,
          li: ({ children }) => <li>{renderHighlight(children)}</li>,
          h1: ({ children }) => <h1>{renderHighlight(children)}</h1>,
          h2: ({ children }) => <h2>{renderHighlight(children)}</h2>,
          h3: ({ children }) => <h3>{renderHighlight(children)}</h3>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  )
}

function renderHighlight(node: React.ReactNode): React.ReactNode {
  if (typeof node === 'string') return parseHighlight(node)
  if (Array.isArray(node)) return node.map((n, i) => <span key={i}>{renderHighlight(n)}</span>)
  return node
}

function parseHighlight(text: string): React.ReactNode {
  if (!text.includes('==')) return text
  const parts = text.split(/(==[^=]+==)/g)
  return parts.map((p, i) => {
    if (p.startsWith('==') && p.endsWith('==')) {
      return (
        <mark key={i} className="bg-state-warning/20 text-text-base rounded px-0.5">
          {p.slice(2, -2)}
        </mark>
      )
    }
    return <span key={i}>{p}</span>
  })
}
